import { useEffect, lazy, Suspense, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PushNotificationProvider } from "./contexts/PushNotificationContext";
import Layout from "./components/Layout";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import PublicPage from "./pages/PublicPage";
import LandingPage from "./pages/LandingPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import UpdateAvailableBanner from "./components/UpdateAvailableBanner";
import OfflineIndicator from "./components/OfflineIndicator";
import useServiceWorkerUpdate from "./hooks/useServiceWorkerUpdate";
import "./App.css";

// Route-level code splitting — each page loads only when navigated to
// Auto-retry with page reload on chunk load failures (happens after deployments)
// Uses sessionStorage to prevent infinite reload loops
const lazyRetry = (importFn) => lazy(() =>
  importFn().catch((error) => {
    const reloadKey = "chunk_reload_" + window.location.pathname;
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, "1");
      window.location.reload();
      return new Promise(() => {});
    }
    sessionStorage.removeItem(reloadKey);
    throw error;
  })
);

const Features = lazyRetry(() => import("./pages/Features"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const FeaturesQuoting = lazyRetry(() => import("./pages/features/FeaturesQuoting"));
const FeaturesProjects = lazyRetry(() => import("./pages/features/FeaturesProjects"));
const ProjectManagerShowcase = lazyRetry(() => import("./pages/ProjectManagerShowcase"));
const FeaturesAdditional = lazyRetry(() => import("./pages/features/FeaturesAdditional"));
const FeaturesAIReceptionist = lazyRetry(() => import("./pages/features/FeaturesAIReceptionist"));
const FeaturesEmergencyCallouts = lazyRetry(() => import("./pages/features/FeaturesEmergencyCallouts"));
const FeaturesOwnerMode = lazyRetry(() => import("./pages/features/FeaturesOwnerMode"));
const FeaturesCrew = lazyRetry(() => import("./pages/features/FeaturesCrew"));
const PublicRoadmap = lazyRetry(() => import("./pages/PublicRoadmap"));
const AdminRoadmap = lazyRetry(() => import("./pages/AdminRoadmap"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const Customers = lazyRetry(() => import("./pages/Customers"));
const EmergencyCallouts = lazyRetry(() => import("./pages/EmergencyCallouts"));
const Voicemails = lazyRetry(() => import("./pages/Voicemails"));
const CustomerDetail = lazyRetry(() => import("./pages/CustomerDetail"));
const CustomerSignup = lazyRetry(() => import("./pages/CustomerSignup"));
const BookQuote = lazyRetry(() => import("./pages/BookQuote"));
const PublicFirmBooking = lazyRetry(() => import("./pages/PublicFirmBooking"));
const PublicJobBookingOffer = lazyRetry(() => import("./pages/PublicJobBookingOffer"));
const PublicQuoteBookingOffer = lazyRetry(() => import("./pages/PublicQuoteBookingOffer"));
const FirmBookingsSettings = lazyRetry(() => import("./pages/FirmBookingsSettings"));
const FirmBookingsInbox = lazyRetry(() => import("./pages/FirmBookingsInbox"));
const JobBookingsSettings = lazyRetry(() => import("./pages/JobBookingsSettings"));
const JobBookingsDashboard = lazyRetry(() => import("./pages/JobBookingsDashboard"));
const SchedulePage = lazyRetry(() => import("./pages/SchedulePage"));
const EmergencyCallout = lazyRetry(() => import("./pages/EmergencyCallout"));
const PersistentEmergencyIntake = lazyRetry(() => import("./pages/PersistentEmergencyIntake"));
const QSHelpOptOut = lazyRetry(() => import("./pages/QSHelpOptOut"));
const QSHelpShortRedirect = lazyRetry(() => import("./pages/QSHelpShortRedirect"));
const Quotes = lazyRetry(() => import("./pages/Quotes"));
const QuoteDetail = lazyRetry(() => import("./pages/QuoteDetail"));
const QuoteAccepted = lazyRetry(() => import("./pages/QuoteAccepted"));
const DepositPaid = lazyRetry(() => import("./pages/DepositPaid"));
const Invoices = lazyRetry(() => import("./pages/Invoices"));
const InvoiceDetail = lazyRetry(() => import("./pages/InvoiceDetail"));
const InvoicePaid = lazyRetry(() => import("./pages/InvoicePaid"));
const SquareCallback = lazyRetry(() => import("./pages/SquareCallback"));
const Items = lazyRetry(() => import("./pages/Items"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const Login = lazyRetry(() => import("./pages/Login"));
const Register = lazyRetry(() => import("./pages/Register"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const PaymentSuccess = lazyRetry(() => import("./pages/PaymentSuccess"));
const AdminDashboard = lazyRetry(() => import("./pages/AdminDashboard"));
const AdminBillingAudit = lazyRetry(() => import("./pages/AdminBillingAudit"));
const AdminStickerOrders = lazyRetry(() => import("./pages/AdminStickerOrders"));
const UpdateCard = lazyRetry(() => import("./pages/UpdateCard"));
const CustomerEmergencyUpdate = lazyRetry(() => import("./pages/CustomerEmergencyUpdate"));
const Reports = lazyRetry(() => import("./pages/Reports"));
const Mileage = lazyRetry(() => import("./pages/Mileage"));
const Support = lazyRetry(() => import("./pages/Support"));
const SupplierQuotePage = lazyRetry(() => import("./pages/SupplierQuotePage"));
const AccountantConnect = lazyRetry(() => import("./pages/AccountantConnect"));
const AccountantConnected = lazyRetry(() => import("./pages/AccountantConnect").then(m => ({ default: m.AccountantConnected })));
const FeatureRequests = lazyRetry(() => import("./pages/FeatureRequests"));
const PremiumOnboarding = lazyRetry(() => import("./pages/PremiumOnboarding"));
const Projects = lazyRetry(() => import("./pages/Projects"));
const ProjectEditor = lazyRetry(() => import("./pages/ProjectEditor"));
const ProjectManager = lazyRetry(() => import("./pages/ProjectManager"));
const ProjectManagerDetail = lazyRetry(() => import("./pages/ProjectManagerDetail"));
const Dictaphone = lazyRetry(() => import("./pages/Dictaphone"));
const ChatConversations = lazyRetry(() => import("./pages/ChatConversations"));
const VideoViewer = lazyRetry(() => import("./pages/VideoViewer"));
const ProposalPreview = lazyRetry(() => import("./pages/ProposalPreview"));
const PublicQuoteView = lazyRetry(() => import("./pages/PublicQuoteView"));
const PublicInvoiceView = lazyRetry(() => import("./pages/PublicInvoiceView"));
const PublicProposalView = lazyRetry(() => import("./pages/PublicProposalView"));
const BuildInvitePortal = lazyRetry(() => import("./pages/BuildInvitePortal"));
const PublicBuildTimeline = lazyRetry(() => import("./pages/PublicBuildTimeline"));
const DemoPreview = lazyRetry(() => import("./pages/DemoPreview"));
const DemoProposal = lazyRetry(() => import("./pages/DemoProposal"));
const SupplierDemo = lazyRetry(() => import("./pages/SupplierDemo"));
const SupplierDemoResult = lazyRetry(() => import("./pages/SupplierDemoResult"));
const BlogManagement = lazyRetry(() => import("./pages/BlogManagement"));
const BlogListing = lazyRetry(() => import("./pages/Blog").then(m => ({ default: m.BlogListing })));
const BlogPost = lazyRetry(() => import("./pages/Blog").then(m => ({ default: m.BlogPost })));
const AmbassadorSignup = lazyRetry(() => import("./pages/AmbassadorSignup"));
const AmbassadorDashboard = lazyRetry(() => import("./pages/AmbassadorDashboard"));
const ResellerSignup = lazyRetry(() => import("./pages/ResellerSignup"));
const ResellerDashboard = lazyRetry(() => import("./pages/ResellerDashboard"));
const ResellerInviteSignup = lazyRetry(() => import("./pages/ResellerInviteSignup"));
const Receipts = lazyRetry(() => import("./pages/Receipts"));
const AccountantDashboard = lazyRetry(() => import("./pages/AccountantDashboard"));
const AccountantClientView = lazyRetry(() => import("./pages/AccountantClientView"));
const AccountantInvite = lazyRetry(() => import("./pages/AccountantInvite"));
const CertificatesDashboard = lazyRetry(() => import("./pages/CertificatesDashboard"));
const CertificateStyleSettings = lazyRetry(() => import("./pages/CertificateStyleSettings"));
const NewCertificate = lazyRetry(() => import("./pages/NewCertificate"));
const CertificateDetails = lazyRetry(() => import("./pages/CertificateDetails"));
const CertificateCircuits = lazyRetry(() => import("./pages/CertificateCircuits"));
const CertificateSupply = lazyRetry(() => import("./pages/CertificateSupply"));
const CertificateInspections = lazyRetry(() => import("./pages/CertificateInspections"));
const CertificateObservations = lazyRetry(() => import("./pages/CertificateObservations"));
const CertificateEVCharger = lazyRetry(() => import("./pages/CertificateEVCharger"));
const CertificateDeclaration = lazyRetry(() => import("./pages/CertificateDeclaration"));
const CertificateAppendix = lazyRetry(() => import("./pages/CertificateAppendix"));
const AIScanner = lazyRetry(() => import("./pages/AIScanner"));
const HPAIScanner = lazyRetry(() => import("./pages/HPAIScanner"));
const BusinessCardMaker = lazyRetry(() => import("./pages/BusinessCardMaker"));
const EmailCampaigns = lazyRetry(() => import("./pages/EmailCampaigns"));
const CampaignDetails = lazyRetry(() => import("./pages/CampaignDetails"));
const MarketingLeaflets = lazyRetry(() => import("./pages/MarketingLeaflets"));
const LeafletBuilder = lazyRetry(() => import("./pages/LeafletBuilder"));
const ContactStickers = lazyRetry(() => import("./pages/ContactStickers"));
const Unsubscribe = lazyRetry(() => import("./pages/Unsubscribe"));
const TeamManagement = lazyRetry(() => import("./pages/TeamManagement"));
const AcceptInvitation = lazyRetry(() => import("./pages/AcceptInvitation"));
const MySchedule = lazyRetry(() => import("./pages/MySchedule"));
const TeamSchedule = lazyRetry(() => import("./pages/TeamSchedule"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const CertificatesSubscriptionSuccess = lazyRetry(() => import("./pages/CertificatesSubscriptionSuccess"));
const TradeCertificatesShowcase = lazyRetry(() => import("./pages/TradeCertificatesShowcase"));
const HeatingPlumbingShowcase = lazyRetry(() => import("./pages/HeatingPlumbingShowcase"));
const PATTestingShowcase = lazyRetry(() => import("./pages/PATTestingShowcase"));
const NewHeatingPlumbingCertificate = lazyRetry(() => import("./pages/NewHeatingPlumbingCertificate"));
const CP12CertificateDetails = lazyRetry(() => import("./pages/CP12CertificateDetails"));
const G3UnventedCertificateDetails = lazyRetry(() => import("./pages/G3UnventedCertificateDetails"));
const GasInstallationCertificateDetails = lazyRetry(() => import("./pages/GasInstallationCertificateDetails"));
const PlumbingWorkCertificateDetails = lazyRetry(() => import("./pages/PlumbingWorkCertificateDetails"));
const PATCertificatesDashboard = lazyRetry(() => import("./pages/PATCertificatesDashboard"));
const PATStickers = lazyRetry(() => import("./pages/PATStickers"));
const InstallationStickers = lazyRetry(() => import("./pages/InstallationStickers"));
const RenewalsHub = lazyRetry(() => import("./pages/RenewalsHub"));
const PublicPATVerify = lazyRetry(() => import("./pages/PublicPATVerify"));
const DirectoryJobAction = lazyRetry(() => import("./pages/DirectoryJobAction"));
const PublicInstallVerify = lazyRetry(() => import("./pages/PublicInstallVerify"));
const NewPATCertificate = lazyRetry(() => import("./pages/NewPATCertificate"));
const PATCertificateDetails = lazyRetry(() => import("./pages/PATCertificateDetails"));
const PageEditor = lazyRetry(() => import("./pages/PageEditor"));
const Inbox = lazyRetry(() => import("./pages/Inbox"));
const FindTradesperson = lazyRetry(() => import("./pages/FindTradesperson"));
const SEOLandingPage = lazyRetry(() => import("./pages/SEOLandingPage"));
const FeaturesPages = lazyRetry(() => import("./pages/features/FeaturesPages"));
const DiscountLanding = lazyRetry(() => import("./pages/DiscountLanding"));

// Protected Route Component
const ProtectedRoute = ({ children, allowAccountantOnly = false, allowRestrictedOrg = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/landing" replace />;
  }
  
  // Accountant-only users can only access accountant routes
  if (user?.accountant_only && !allowAccountantOnly) {
    return <Navigate to="/accountant" replace />;
  }
  
  // Restricted org members (cert_user, staff) can only access certificates and settings
  const isRestrictedOrgMember = user?.org_role && user.org_role !== "owner";
  if (isRestrictedOrgMember && !allowRestrictedOrg) {
    // Check if the current path is allowed
    const allowedPaths = ['/certificates', '/settings'];
    const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));
    if (!isAllowedPath) {
      return <Navigate to="/certificates" replace />;
    }
  }
  
  return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Accountant-only users can ONLY access the accountant portal
    if (user?.accountant_only) {
      return <Navigate to="/accountant" replace />;
    }
    // Restricted org members (cert_user, staff) go to certificates
    if (user?.org_role && user.org_role !== "owner") {
      return <Navigate to="/certificates" replace />;
    }
    // Honour ?redirect=/some-app-path (e.g. showcase CTAs send users to
    // /login?redirect=/certificates so they land on the right page once
    // logged in). Only same-origin app paths are accepted.
    const rawRedirect = new URLSearchParams(location.search).get("redirect");
    const safeRedirect = rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
      ? rawRedirect
      : null;
    if (safeRedirect) {
      return <Navigate to={safeRedirect} replace />;
    }
    // Regular accountants (who also have a business account) go to main dashboard
    // They can access /accountant from the menu
    return <Navigate to="/" replace />;
  }

  return children;
};

// Lightweight loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="page-loader">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <ChunkErrorBoundary>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public routes (no auth) */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/features/quoting" element={<FeaturesQuoting />} />
      <Route path="/features/projects" element={<FeaturesProjects />} />
      <Route path="/features/project-manager" element={<ProjectManagerShowcase />} />
      <Route path="/features/additional" element={<FeaturesAdditional />} />
      <Route path="/features/ai-receptionist" element={<FeaturesAIReceptionist />} />
      <Route path="/features/emergency-callouts" element={<FeaturesEmergencyCallouts />} />
      <Route path="/features/owner-mode" element={<FeaturesOwnerMode />} />
      <Route path="/features/crew" element={<FeaturesCrew />} />
      <Route path="/features/pages" element={<FeaturesPages />} />
      <Route path="/demo-preview" element={<DemoPreview />} />
      <Route path="/demo-proposal" element={<DemoProposal />} />
      <Route path="/demo-proposal/:leadId" element={<DemoProposal />} />
      <Route path="/quote-accepted" element={<QuoteAccepted />} />
      <Route path="/deposit-paid" element={<DepositPaid />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/invoice-paid" element={<InvoicePaid />} />
      <Route path="/api/square/callback" element={<SquareCallback />} />
      <Route path="/customers-signup/:userId" element={<CustomerSignup />} />
      <Route path="/book/:userId" element={<BookQuote />} />
      <Route path="/firm-book/:token" element={<PublicFirmBooking />} />
      <Route path="/firm-book/i/:token" element={<PublicFirmBooking />} />
      <Route path="/job-booking/:slug" element={<PublicJobBookingOffer />} />
      <Route path="/job-booking/:companySlug/:slug" element={<PublicJobBookingOffer />} />
      <Route path="/quote-booking/:token" element={<PublicQuoteBookingOffer />} />
      <Route path="/e/:token" element={<EmergencyCallout />} />
      <Route path="/help/:slugOrToken" element={<PersistentEmergencyIntake />} />
      <Route path="/h/:slugOrToken" element={<PersistentEmergencyIntake />} />
      <Route path="/qshelp/stop/:token" element={<QSHelpOptOut />} />
      <Route path="/qs/:shortId" element={<QSHelpShortRedirect />} />
      <Route path="/supplier-quote/:token" element={<SupplierQuotePage />} />
      <Route path="/accountant-connect/:token" element={<AccountantConnect />} />
      <Route path="/accountant-connected" element={<AccountantConnected />} />
      <Route path="/proposal/:id" element={<ProposalPreview />} />
      <Route path="/view/quote/:quoteId" element={<PublicQuoteView />} />
      <Route path="/view/invoice/:invoiceId" element={<PublicInvoiceView />} />
      <Route path="/p/:slug" element={<PublicPage />} />
      <Route path="/verify/pat/:code" element={<PublicPATVerify />} />
      <Route path="/verify/install/:code" element={<PublicInstallVerify />} />
      <Route path="/find" element={<FindTradesperson />} />
      <Route path="/directory/job/verify/:token" element={<DirectoryJobAction action="verify" />} />
      <Route path="/directory/job/stop/:token" element={<DirectoryJobAction action="stop" />} />
      <Route path="/directory/interest/:token" element={<DirectoryJobAction action="interest" />} />
      <Route path="/find/:trade/:location" element={<SEOLandingPage />} />
      <Route path="/view/proposal/:proposalId" element={<PublicProposalView />} />
      <Route path="/build/invite/:token" element={<BuildInvitePortal />} />
      <Route path="/track/:token" element={<PublicBuildTimeline />} />
      <Route path="/view/video/:recordingId" element={<VideoViewer />} />
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      <Route path="/billing/update-card/:token" element={<UpdateCard />} />
      <Route path="/qs/u/:shortId" element={<CustomerEmergencyUpdate />} />
      <Route path="/emergency-update/:token" element={<CustomerEmergencyUpdate />} />
      
      {/* Supplier Demo Routes */}
      <Route path="/supplier-demo/:demoId" element={<SupplierDemo />} />
      <Route path="/supplier-demo/:demoId/result" element={<SupplierDemoResult />} />
      
      {/* Blog Routes (Public) */}
      <Route path="/blog" element={<BlogListing />} />
      <Route path="/blog/:category" element={<BlogListing />} />
      <Route path="/blog/post/:slug" element={<BlogPost />} />
      
      {/* Ambassador Signup (Public) */}
      <Route path="/ambassador-signup" element={<AmbassadorSignup />} />
      
      {/* Reseller Signup (Public) */}
      <Route path="/become-reseller" element={<ResellerSignup />} />
      
      {/* Reseller Invite Signup (Public - Admin Invite) */}
      <Route path="/reseller-signup" element={<ResellerInviteSignup />} />
      
      {/* Team Invitation Accept (Public - email link) */}
      <Route path="/accept-invite/:token" element={<AcceptInvitation />} />
      
      {/* Discount Landing (Public - group discount links) */}
      <Route path="/go/:slug" element={<DiscountLanding />} />
      
      {/* Privacy Policy (Public) */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/roadmap" element={<PublicRoadmap />} />
      
      {/* Trade Certificates Showcase (Public) */}
      <Route path="/trade-certificates" element={<TradeCertificatesShowcase />} />
      <Route path="/trade-certificates/electrical" element={<TradeCertificatesShowcase />} />
      <Route path="/trade-certificates/heating-plumbing" element={<HeatingPlumbingShowcase />} />
      <Route path="/trade-certificates/pat-testing" element={<PATTestingShowcase />} />
      
      {/* Accountant Routes */}
      <Route path="/accountant-invite" element={<AccountantInvite />} />
      <Route path="/accountant" element={
        <ProtectedRoute allowAccountantOnly={true}>
          <AccountantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/accountant/client/:clientId" element={
        <ProtectedRoute allowAccountantOnly={true}>
          <AccountantClientView />
        </ProtectedRoute>
      } />
      
      {/* Auth routes - redirect to dashboard if logged in */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Protected app routes with layout */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="enquiries" element={<Inbox />} />
        <Route path="customers" element={<Customers />} />
        <Route path="emergency-callouts" element={<EmergencyCallouts />} />
        <Route path="voicemails" element={<Voicemails />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="quotes/:id" element={<QuoteDetail />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="items" element={<Items />} />
        <Route path="reports" element={<Reports />} />
        <Route path="mileage" element={<Mileage />} />
        <Route path="receipts" element={<Receipts />} />
        <Route path="feature-requests" element={<FeatureRequests />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/firm-bookings" element={<FirmBookingsSettings />} />
        <Route path="settings/job-bookings" element={<JobBookingsSettings />} />
        <Route path="job-bookings/bookings" element={<JobBookingsDashboard />} />
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="firm-bookings/inbox" element={<FirmBookingsInbox />} />
        <Route path="my-page" element={<PageEditor />} />
        <Route path="onboarding" element={<PremiumOnboarding />} />
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="admin/roadmap" element={<AdminRoadmap />} />
        <Route path="admin/billing-audit" element={<AdminBillingAudit />} />
        <Route path="admin/sticker-orders" element={<AdminStickerOrders />} />
        <Route path="admin/blog" element={<BlogManagement />} />
        <Route path="ambassador" element={<AmbassadorDashboard />} />
        <Route path="reseller-dashboard" element={<ResellerDashboard />} />
        <Route path="support" element={<Support />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectEditor />} />
        <Route path="proposals" element={<Projects />} />
        <Route path="proposals/:id" element={<ProjectEditor />} />
        <Route path="project-manager" element={<ProjectManager />} />
        <Route path="project-manager/:id" element={<ProjectManagerDetail />} />
        <Route path="dictaphone" element={<Dictaphone />} />
        <Route path="chat-conversations" element={<ChatConversations />} />
        <Route path="certificates" element={<CertificatesDashboard />} />
        <Route path="certificates/style" element={<CertificateStyleSettings />} />
        <Route path="certificates/new" element={<NewCertificate />} />
        <Route path="certificates/scanner" element={<AIScanner />} />
        <Route path="certificates/hp-scanner" element={<HPAIScanner />} />
        <Route path="certificates/team" element={<TeamManagement />} />
        <Route path="certificates/schedule" element={<TeamSchedule />} />
        <Route path="certificates/my-schedule" element={<MySchedule />} />
        <Route path="certificates/subscription/success" element={<CertificatesSubscriptionSuccess />} />
        {/* Heating & Plumbing Certificate Routes */}
        <Route path="certificates/heating-plumbing/new" element={<NewHeatingPlumbingCertificate />} />
        <Route path="certificates/heating-plumbing/cp12/:id" element={<CP12CertificateDetails />} />
        <Route path="certificates/heating-plumbing/g3/:id" element={<G3UnventedCertificateDetails />} />
        <Route path="certificates/heating-plumbing/gas-installation/:id" element={<GasInstallationCertificateDetails />} />
        <Route path="certificates/heating-plumbing/plumbing-work/:id" element={<PlumbingWorkCertificateDetails />} />
        {/* PAT Testing Certificate Routes */}
        <Route path="certificates/renewals" element={<RenewalsHub />} />
        <Route path="certificates/pat" element={<PATCertificatesDashboard />} />
        <Route path="certificates/pat/new" element={<NewPATCertificate />} />
        <Route path="certificates/pat/stickers" element={<PATStickers />} />
        <Route path="certificates/installations/stickers" element={<InstallationStickers />} />
        <Route path="certificates/pat/retest-due" element={<Navigate to="/certificates/renewals?category=pat" replace />} />
        <Route path="certificates/pat/:id" element={<PATCertificateDetails />} />
        {/* Electrical Certificate Routes */}
        <Route path="certificates/:id/details" element={<CertificateDetails />} />
        <Route path="certificates/:id/supply" element={<CertificateSupply />} />
        <Route path="certificates/:id/circuits" element={<CertificateCircuits />} />
        <Route path="certificates/:id/inspections" element={<CertificateInspections />} />
        <Route path="certificates/:id/observations" element={<CertificateObservations />} />
        <Route path="certificates/:id/ev-charger" element={<CertificateEVCharger />} />
        <Route path="certificates/:id/declaration" element={<CertificateDeclaration />} />
        <Route path="certificates/:id/appendix" element={<CertificateAppendix />} />
        {/* Marketing Tools */}
        <Route path="marketing/business-cards" element={<BusinessCardMaker />} />
        <Route path="marketing/email-campaigns" element={<EmailCampaigns />} />
        <Route path="marketing/campaigns/:campaignId" element={<CampaignDetails />} />
        <Route path="marketing/leaflets" element={<LeafletBuilder />} />
        <Route path="marketing/leaflets/quotesorted" element={<MarketingLeaflets />} />
        <Route path="marketing/contact-stickers" element={<ContactStickers />} />
      </Route>
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
    </>
  );
}

function ServiceWorkerUpdateGate() {
  const { updateReady, applyUpdate } = useServiceWorkerUpdate();
  const [dismissed, setDismissed] = useState(false);
  if (!updateReady || dismissed) return null;
  return (
    <UpdateAvailableBanner
      onRefresh={applyUpdate}
      onDismiss={() => setDismissed(true)}
    />
  );
}

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <PushNotificationProvider>
              <OfflineIndicator />
              <AppRoutes />
              <PWAInstallPrompt />
              <ServiceWorkerUpdateGate />
            </PushNotificationProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </div>
    </HelmetProvider>
  );
}

export default App;